import { useEffect, useRef, useState } from "react";
import { useLocale } from "../hooks/useLocale";
import styles from "./Modal.module.css";

interface Props {
  initialName?: string;
  onSubmit: (name: string) => void | Promise<void>;
  onClose: () => void;
}

export function SaveModal({ initialName = "", onSubmit, onClose }: Props) {
  const { t } = useLocale();
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(trimmed);
    } catch (caught) {
      setErr((caught as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className={styles.backdrop} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <h2 className={styles.title}>{t("save.title")}</h2>
        <form onSubmit={submit} className={styles.body}>
          <label className={styles.label}>
            <span>{t("save.name")}</span>
            <input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={255}
              required
            />
          </label>
          {err && <p className={styles.error}>{err}</p>}
          <div className={styles.actions}>
            <button type="button" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </button>
            <button type="submit" className={styles.primary} disabled={busy || !name.trim()}>
              {busy ? t("common.loading") : t("save.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
